import type {
  TConversationDetailsSchema,
  TTranslateConversationResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import {
  LANGUAGE_FLAG_EMOJIS,
  TRANSLATION_LANGUAGE_NAMES,
} from '@conversed/shared/src/central/constants/languages';
import { isDutchText } from '@helpers/languageDetectionHelper';
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerEditorHandle } from '../../AnswerEditor';
import {
  getDisplayedAnswer,
  getDisplayedQuestion,
} from '../conversation-review.helpers';
import type {
  TDraftState,
  TTranslationState,
} from '../conversation-review-modal.type';

type TTranslateConversationFn = (
  id: string,
  question: string,
  answer: string,
) => Promise<TTranslateConversationResponse | undefined>;

type TUseConversationTranslationParams = {
  currentConversation: TConversationDetailsSchema | undefined;
  currentCorrectedAnswer: string;
  hasMultilingualTrainAI: boolean;
  answerEditorRef: RefObject<AnswerEditorHandle | null>;
  translateConversation: TTranslateConversationFn;
  setCurrentDraft: (updater: (prev: TDraftState) => TDraftState) => void;
};

export const useConversationTranslation = ({
  currentConversation,
  currentCorrectedAnswer,
  hasMultilingualTrainAI,
  answerEditorRef,
  translateConversation,
  setCurrentDraft,
}: TUseConversationTranslationParams) => {
  const { t } = useTranslation();

  const [translationState, setTranslationState] =
    useState<TTranslationState | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);

  // Tracks the active conversation ID to guard async translation callbacks against stale closures
  const activeConversationIdRef = useRef<string | undefined>(undefined);
  // Saves the correction text in the original language before switching to translated mode
  const originalCorrectedAnswerRef = useRef<string>('');
  // Saves the Dutch correction when toggling back to original so it can be restored on re-toggle
  const dutchCorrectionRef = useRef<string>('');

  // Reset translation state and sync editor on each conversation change.
  // When the multilingual feature is off, skip the cached-translation path entirely
  // so no translation chrome (badge, original-language card, translated-to-Dutch note) renders.
  useEffect(() => {
    activeConversationIdRef.current = currentConversation?._id;
    originalCorrectedAnswerRef.current = '';
    dutchCorrectionRef.current = '';

    if (hasMultilingualTrainAI && currentConversation?.translation) {
      const { question, answer, sourceLang } = currentConversation.translation;
      setTranslationState({
        sourceLanguageName:
          TRANSLATION_LANGUAGE_NAMES[sourceLang] ?? sourceLang,
        sourceLang,
        translatedQuestion: question,
        translatedAnswer: answer,
      });
      // Pre-populate as Dutch view since translation is already cached
      setShowTranslated(true);
    } else {
      setTranslationState(null);
      setShowTranslated(false);
    }
    setIsTranslating(false);
  }, [currentConversation, hasMultilingualTrainAI]);

  const switchToTranslated = useCallback(
    (dutchAnswer: string) => {
      // Only capture the original correction on the first toggle; re-toggles must not overwrite it
      if (!showTranslated) {
        originalCorrectedAnswerRef.current = currentCorrectedAnswer;
      }
      const savedDutchCorrection = dutchCorrectionRef.current;
      dutchCorrectionRef.current = '';

      if (savedDutchCorrection?.trim()) {
        // Reviewer had typed a Dutch correction before toggling back - restore it
        setCurrentDraft((prev) => ({
          ...prev,
          correctedAnswer: savedDutchCorrection,
        }));
        answerEditorRef.current?.setEditorText(savedDutchCorrection);
      } else if (
        !currentCorrectedAnswer?.trim() ||
        currentCorrectedAnswer === currentConversation?.answer
      ) {
        // Auto-fill with machine translation if the reviewer has not typed a custom correction
        setCurrentDraft((prev) => ({
          ...prev,
          correctedAnswer: dutchAnswer,
        }));
        answerEditorRef.current?.setEditorText(dutchAnswer);
      }
      setShowTranslated(true);
    },
    [
      showTranslated,
      currentCorrectedAnswer,
      currentConversation?.answer,
      setCurrentDraft,
      answerEditorRef,
    ],
  );

  const handleTranslate = useCallback(async () => {
    if (!currentConversation) {
      return;
    }
    if (translationState) {
      if (showTranslated) {
        // Save the Dutch correction before restoring original-language text
        dutchCorrectionRef.current = currentCorrectedAnswer;
        const restoredAnswer = originalCorrectedAnswerRef.current;
        setCurrentDraft((prev) => ({
          ...prev,
          correctedAnswer: restoredAnswer,
        }));
        answerEditorRef.current?.setEditorText(restoredAnswer);
        setShowTranslated(false);
      } else {
        switchToTranslated(translationState.translatedAnswer);
      }
      return;
    }
    const conversationId = currentConversation._id;
    setIsTranslating(true);
    try {
      const result = await translateConversation(
        currentConversation._id,
        currentConversation.question,
        currentConversation.answer,
      );
      if (result && conversationId === activeConversationIdRef.current) {
        const newTranslationState: TTranslationState = {
          sourceLanguageName: result.sourceLanguageName,
          sourceLang: result.sourceLang,
          translatedQuestion: result.translatedQuestion,
          translatedAnswer: result.translatedAnswer,
        };
        setTranslationState(newTranslationState);
        switchToTranslated(result.translatedAnswer);
      }
    } finally {
      setIsTranslating(false);
    }
  }, [
    translationState,
    showTranslated,
    currentCorrectedAnswer,
    currentConversation,
    translateConversation,
    switchToTranslated,
    setCurrentDraft,
    answerEditorRef,
  ]);

  const translateButtonLabel = useMemo((): string => {
    if (!translationState) {
      return t('train-ai.conversation-review.language.translate');
    }
    // "View Original X" uses the source language flag - it indicates what you're switching to.
    // "View Dutch" uses the Dutch flag for the same reason.
    if (showTranslated) {
      const sourceFlag =
        LANGUAGE_FLAG_EMOJIS[translationState.sourceLang] ?? '🌐';
      return `${sourceFlag} ${t(
        'train-ai.conversation-review.language.view-original',
        { language: translationState.sourceLanguageName },
      )}`;
    }
    const dutchFlag = LANGUAGE_FLAG_EMOJIS.nl ?? '🇳🇱';
    return `${dutchFlag} ${t('train-ai.conversation-review.language.view-dutch')}`;
  }, [translationState, showTranslated, t]);

  // Detect if the conversation Q+A is already in Dutch so we can hide the Translate
  // button - translating Dutch->Dutch would be a no-op and confusing for the reviewer.
  const isCurrentConversationDutch = useMemo(
    () =>
      isDutchText(currentConversation?.question) ||
      isDutchText(currentConversation?.answer),
    [currentConversation?.question, currentConversation?.answer],
  );

  const isShowingOriginalLanguage = Boolean(
    translationState && !showTranslated,
  );

  const displayedQuestion = getDisplayedQuestion(
    currentConversation,
    translationState,
    showTranslated,
  );
  const displayedAnswer = getDisplayedAnswer(
    currentConversation,
    translationState,
    showTranslated,
  );

  return {
    translationState,
    showTranslated,
    isTranslating,
    handleTranslate,
    translateButtonLabel,
    isCurrentConversationDutch,
    isShowingOriginalLanguage,
    displayedQuestion,
    displayedAnswer,
  };
};
